import { useState, useCallback, useEffect, useMemo } from 'react';
import { useForm, Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { planTemplateFormSchema } from '../../../validations/reunification.validation';
import {
  usePlanTemplates,
  usePlanTemplate,
  useCreatePlanTemplate,
  useUpdatePlanTemplate,
  useDeletePlanTemplate,
  useClonePlanTemplate,
} from '../../../hooks/queries/useReunification';
import type {
  PlanTemplateFormValues,
  PlanTemplateWrite,
  PlanTemplate,
  PlanTemplateDetail,
} from '../../../types/ReunificationTypes';
import { PlanTemplateInitialValues } from '../../../types/ReunificationTypes';
import { toaster } from '../../ui/toaster';
import { ApiValidationError } from '../../../lib/http-client';
import type { StatusType } from '@themes/aegix';

export interface PlanRow {
  [key: string]: unknown;
  id: string;
  name: string;
  incident_type: string;
  reunification_mode: string;
  scale: string;
  status: StatusType;
  sites: number;
  version: number;
}

export function transformPlans(plans: PlanTemplate[]): PlanRow[] {
  return plans.map((plan) => ({
    id: plan.public_id,
    name: plan.name,
    incident_type: plan.incident_type_display,
    reunification_mode: plan.mode_display,
    scale: plan.scale_display,
    status: plan.is_published ? 'active' : ('pending' as StatusType),
    sites: plan.site_count,
    version: plan.version,
  }));
}

export function usePlansCrud() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [sortColumn, setSortColumn] = useState<string>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [actionMode, setActionMode] = useState<'add' | 'edit' | ''>('');
  const [currentRow, setCurrentRow] = useState<PlanRow>();
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [emptySites, setEmptySites] = useState<Set<string>>(new Set());

  const handleFilterChange = useCallback((column: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [column]: value }));
  }, []);

  const handleFilterClear = useCallback((column: string) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
  }, []);

  const handleEmptyTemplate = useCallback((id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setEmptySites((prev) => new Set(prev).add(id));
  }, []);

  const toggleExpand = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Fetch detail data for the edit drawer to get JSONB fields (verification/notification)
  const { data: editDetail } = usePlanTemplate(
    actionMode === 'edit' && currentRow ? currentRow.id : '',
  );

  const createMutation = useCreatePlanTemplate();
  const updateMutation = useUpdatePlanTemplate();
  const deleteMutation = useDeletePlanTemplate();
  const cloneMutation = useClonePlanTemplate();

  const { data, isLoading, isFetching, error } = usePlanTemplates({
    search: searchValue || undefined,
    is_published:
      statusFilter === 'published'
        ? true
        : statusFilter === 'draft'
          ? false
          : undefined,
    page: currentPage,
    page_size: pageSize,
    ordering: sortColumn
      ? `${sortDirection === 'desc' ? '-' : ''}${sortColumn}`
      : undefined,
  });

  const plans = data?.results ? transformPlans(data.results) : [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  const filteredPlans = useMemo(() => {
    const activeFilters = Object.entries(filterValues).filter(([, v]) => v);
    if (activeFilters.length === 0) return plans;
    return plans.filter((row) =>
      activeFilters.every(([col, val]) => {
        const cellValue = String(
          (row as Record<string, unknown>)[col] ?? '',
        ).toLowerCase();
        return cellValue.includes(val.toLowerCase());
      }),
    );
  }, [plans, filterValues]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, statusFilter, pageSize, filterValues]);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((prev) =>
          prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc',
        );
        if (sortDirection === 'desc') setSortColumn(undefined);
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn, sortDirection],
  );

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setActionMode('');
    setCurrentRow(undefined);
  }, []);

  const getInitialValues = (): PlanTemplateFormValues => {
    if (actionMode === 'edit' && currentRow && data) {
      const plan = data?.results?.find((p) => p.public_id === currentRow.id);
      if (plan) {
        const detail = editDetail as PlanTemplateDetail | undefined;
        return {
          name: plan.name,
          description: plan.description,
          incident_type: plan.incident_type,
          reunification_mode: plan.reunification_mode,
          scale: plan.scale,
          default_leo_interview: plan.default_leo_interview,
          default_counseling: plan.default_counseling,
          special_procedures: plan.special_procedures,
          estimated_duration_hours: plan.estimated_duration_hours ?? '',
          is_published: plan.is_published,
          guardian_verification_required:
            detail?.guardian_verification_required ?? true,
          require_id_verification: detail?.require_id_verification ?? false,
          require_photo_capture: detail?.require_photo_capture ?? false,
          require_signature: detail?.require_signature ?? false,
          notify_on_activation: detail?.notify_on_activation ?? true,
          notification_channels: detail?.notification_channels ?? ['push'],
        };
      }
    }
    return PlanTemplateInitialValues;
  };

  const methods = useForm<PlanTemplateFormValues>({
    defaultValues: getInitialValues(),
    resolver: zodResolver(planTemplateFormSchema) as unknown as Resolver<PlanTemplateFormValues>,
    mode: 'onBlur',
  });

  useEffect(() => {
    methods.reset(getInitialValues());
  }, [actionMode, currentRow, data, editDetail]);

  const handleSubmit = (values: PlanTemplateFormValues) => {
    const payload = {
      ...values,
      incident_type: values.incident_type || undefined,
      reunification_mode: values.reunification_mode || undefined,
      scale: values.scale || undefined,
      estimated_duration_hours:
        values.estimated_duration_hours === ''
          ? null
          : values.estimated_duration_hours,
      notification_channels: values.notify_on_activation
        ? values.notification_channels
        : [],
    } as PlanTemplateWrite;

    const handleApiError = (err: Error, label: string) => {
      if (err instanceof ApiValidationError) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          methods.setError(field as keyof PlanTemplateFormValues, {
            message: messages.join(', '),
          });
        }
      }
      toaster.create({
        title: label,
        description: err.message,
        type: 'error',
      });
    };

    if (actionMode === 'edit' && currentRow) {
      updateMutation.mutate(
        { id: currentRow.id, data: payload },
        {
          onSuccess: () => {
            toaster.create({ title: 'Plan updated', type: 'success' });
            handleDrawerClose();
          },
          onError: (err) => handleApiError(err, 'Failed to update plan'),
        },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          toaster.create({ title: 'Plan created', type: 'success' });
          handleDrawerClose();
        },
        onError: (err) => handleApiError(err, 'Failed to create plan'),
      });
    }
  };

  const handleDelete = () => {
    if (!currentRow) return;
    deleteMutation.mutate(currentRow.id, {
      onSuccess: () => {
        toaster.create({ title: 'Plan deleted', type: 'success' });
        handleDrawerClose();
      },
      onError: (err) => {
        toaster.create({
          title: 'Failed to delete plan',
          description: err.message,
          type: 'error',
        });
      },
    });
  };

  const openEditDrawer = useCallback((row: PlanRow) => {
    setActionMode('edit');
    setCurrentRow(row);
    setDrawerOpen(true);
  }, []);

  const openAddDrawer = useCallback(() => {
    setActionMode('add');
    setCurrentRow(undefined);
    setDrawerOpen(true);
  }, []);

  return {
    // Table state
    statusFilter,
    setStatusFilter,
    searchValue,
    setSearchValue,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    sortColumn,
    sortDirection,
    filterValues,
    expandedRows,
    emptySites,

    // Table handlers
    handleSort,
    handleFilterChange,
    handleFilterClear,
    handleEmptyTemplate,
    toggleExpand,

    // Data
    plans: filteredPlans,
    totalItems,
    totalPages,
    isLoading,
    isFetching,
    error,

    // Drawer state
    drawerOpen,
    actionMode,
    currentRow,
    handleDrawerClose,
    openEditDrawer,
    openAddDrawer,

    // Form
    methods,
    handleSubmit,
    handleDelete,
  };
}
